import { Form, type FormRule } from "antd";
import type { HTMLAttributes } from "react";
import { useIntl } from "react-intl";
import { FormInput } from "~components/Form/FormInput";
import { FormItem } from "~components/Form/FormItem";
import { FormSection, type FormSectionLabel } from "~components/Form/FormSection";
import { FormSelectYesNo } from "~components/Form/FormSelectYesNo";
import { FormSkeleton } from "~components/Form/FormSkeleton";
import { SelectFixtureProfile } from "~components/SelectFixtureProfile/SelectFixtureProfile";
import { SelectPool } from "~components/SelectPool/SelectPool";
import { SelectSeries } from "~components/SelectSeries/SelectSeries";
import { SelectStage } from "~components/SelectStage/SelectStage";
import { useGetFixture } from "~pages/Fixtures/hooks";
import type { EditFixtureForm } from "~pages/Fixtures/types";
import { StyledFixtureAdvancedSection } from "./FixtureAdvancedSection.styles";

type Props = HTMLAttributes<HTMLDivElement> & {
  isUpdating: boolean;
};

export const FixtureAdvancedSection = ({ isUpdating, ...props }: Props) => {
  const { formatMessage } = useIntl();
  const form = Form.useFormInstance<EditFixtureForm>();
  const { isLoading, isFetching } = useGetFixture();
  const stageCode = Form.useWatch("stageCode", form)?.value;

  const fields: FormSectionLabel[] = [
    {
      titleIntlKey: "name.local",
      descriptionIntlKey: { key: "form.help.local.name.item", item: "match" },
    },
    {
      titleIntlKey: "name.latin",
      descriptionIntlKey: { key: "form.help.latin.name.item", item: "match" },
    },
    {
      titleIntlKey: "startTimeUTC",
      descriptionIntlKey: "form.help.fixture.actual.start.time",
    },
    {
      titleIntlKey: "roundNumber",
      descriptionIntlKey: "form.help.fixture.round",
    },
    { titleIntlKey: "stage", descriptionIntlKey: "form.help.fixture.stage" },
    { titleIntlKey: "pool", descriptionIntlKey: "form.help.fixture.pool" },
    { titleIntlKey: "series", descriptionIntlKey: "form.help.fixture.series" },
    {
      titleIntlKey: "externalId",
      descriptionIntlKey: "form.help.fixture.externalId",
    },
    {
      titleIntlKey: "fixtureNumber",
      descriptionIntlKey: "form.help.fixture.number",
    },
    {
      titleIntlKey: "internationalReference",
      descriptionIntlKey: "form.help.fixture.international.reference",
    },
    {
      titleIntlKey: "placingIfLost",
      descriptionIntlKey: "form.help.fixture.placing.if.lost",
    },
    {
      titleIntlKey: "placingIfWon",
      descriptionIntlKey: "form.help.fixture.placing.if.won",
    },
    {
      titleIntlKey: "ticketURL",
      descriptionIntlKey: "form.help.fixture.tickets.url",
    },
    {
      titleIntlKey: "liveDataAvailable",
      descriptionIntlKey: "form.help.fixture.live.data.available",
    },
    {
      titleIntlKey: "timesUnconfirmed",
      descriptionIntlKey: "form.help.time.is.unconfirmed",
    },
    {
      titleIntlKey: "includeInStandings",
      descriptionIntlKey: "form.help.fixture.include.standings",
    },
    {
      titleIntlKey: "liveVideoAvailable",
      descriptionIntlKey: "form.help.fixture.live.video",
    },
    { titleIntlKey: "added", descriptionIntlKey: "form.help.fixture.added" },
    {
      titleIntlKey: "updated",
      descriptionIntlKey: "form.help.fixture.updates",
    },
    { titleIntlKey: "attendance", descriptionIntlKey: "crowd.attendance" },
    {
      titleIntlKey: "sellout",
      descriptionIntlKey: "form.help.fixture.sellout",
    },
  ];

  const maxLength150Rule: FormRule = {
    max: 150,
    message: formatMessage({ id: "form.validation.max.length" }, { length: 150 }),
  };

  const disabled = isUpdating || isFetching;

  return (
    <div {...props}>
      <FormSection title={formatMessage({ id: "fixture.update" })} fields={fields} />
      <StyledFixtureAdvancedSection>
        <FormInput<EditFixtureForm>
          label={formatMessage({ id: "name.local" })}
          name="nameLocal"
          showSkeleton={isLoading}
          disabled={disabled}
          rules={[maxLength150Rule]}
        />
        <div>
          {isLoading ? (
            <FormSkeleton label={formatMessage({ id: "fixture.match.profile" })} />
          ) : (
            <FormItem<EditFixtureForm> layout="vertical" name="profileId" label={formatMessage({ id: "fixture.match.profile" })}>
              <SelectFixtureProfile disabled={disabled} />
            </FormItem>
          )}
        </div>
        <FormSelectYesNo<EditFixtureForm>
          label={formatMessage({ id: "liveDataAvailable" })}
          name="liveDataAvailable"
          showSkeleton={isLoading}
          disabled={disabled}
        />
        <FormInput<EditFixtureForm>
          label={formatMessage({ id: "attendance" })}
          name="attendance"
          showSkeleton={isLoading}
          disabled={disabled}
          inputProps={{ type: "number", min: 0, max: 999_999 }}
          rules={[
            {
              validator: (_, value) => {
                if (Number(value) < 0 || Number(value) > 999_999) {
                  return Promise.reject(
                    formatMessage(
                      {
                        id: "form.validation.attendance.range",
                      },
                      {
                        min: 0,
                        max: 999_999,
                      },
                    ),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        />
        <FormInput<EditFixtureForm>
          label={formatMessage({ id: "name.latin" })}
          name="nameLatin"
          showSkeleton={isLoading}
          disabled={disabled}
          rules={[maxLength150Rule]}
        />
        <FormInput<EditFixtureForm>
          label={formatMessage({ id: "externalId" })}
          name="externalId"
          showSkeleton={isLoading}
          disabled={disabled}
        />
        <FormSelectYesNo<EditFixtureForm>
          label={formatMessage({ id: "timesUnconfirmed" })}
          name="timesUnconfirmed"
          showSkeleton={isLoading}
          disabled={disabled}
        />
        <FormInput<EditFixtureForm>
          label={formatMessage({ id: "fixtureNumber" })}
          name="fixtureNumber"
          showSkeleton={isLoading}
          disabled={disabled}
          inputProps={{ type: "number" }}
        />
        <FormInput<EditFixtureForm> label={formatMessage({ id: "roundNumber" })} name="roundNumber" showSkeleton={isLoading} disabled />
        <FormInput<EditFixtureForm>
          label={formatMessage({ id: "internationalReference" })}
          name="internationalReference"
          showSkeleton={isLoading}
          disabled={disabled}
        />
        <FormSelectYesNo<EditFixtureForm>
          label={formatMessage({ id: "includeInStandings" })}
          name="includeInStandings"
          showSkeleton={isLoading}
          disabled={disabled}
        />
        <FormSelectYesNo<EditFixtureForm>
          label={formatMessage({ id: "sellout" })}
          name="sellout"
          showSkeleton={isLoading}
          disabled={disabled}
        />
        <div>
          {isLoading ? (
            <FormSkeleton label={formatMessage({ id: "stage" })} />
          ) : (
            <FormItem<EditFixtureForm> layout="vertical" name="stageCode" label={formatMessage({ id: "stage" })}>
              <SelectStage onChange={() => form.setFieldValue("seriesCode", null)} disabled={disabled} />
            </FormItem>
          )}
        </div>
        <FormInput<EditFixtureForm>
          label={formatMessage({ id: "placingIfLost" })}
          name="placingIfLost"
          showSkeleton={isLoading}
          disabled={disabled}
          inputProps={{ type: "number" }}
        />
        <FormSelectYesNo<EditFixtureForm>
          label={formatMessage({ id: "liveVideoAvailable" })}
          name="liveVideoAvailable"
          showSkeleton={isLoading}
          disabled
        />
        <FormInput<EditFixtureForm> label={formatMessage({ id: "startTimeUTC" })} name="startTimeUTC" showSkeleton={isLoading} disabled />
        <div>
          {isLoading ? (
            <FormSkeleton label={formatMessage({ id: "pool" })} />
          ) : (
            <FormItem<EditFixtureForm> layout="vertical" name="poolCode" label={formatMessage({ id: "pool" })}>
              <SelectPool disabled={disabled} />
            </FormItem>
          )}
        </div>
        <FormInput<EditFixtureForm>
          label={formatMessage({ id: "placingIfWon" })}
          name="placingIfWon"
          showSkeleton={isLoading}
          disabled={disabled}
          inputProps={{ type: "number" }}
        />
        <FormInput<EditFixtureForm> label={formatMessage({ id: "added" })} name="added" showSkeleton={isLoading} disabled />
        <div>
          {isLoading ? (
            <FormSkeleton label={formatMessage({ id: "series" })} />
          ) : (
            <FormItem<EditFixtureForm> layout="vertical" name="seriesCode" label={formatMessage({ id: "series" })}>
              <SelectSeries stageCode={stageCode} disabled={disabled} />
            </FormItem>
          )}
        </div>
        <FormInput<EditFixtureForm>
          label={formatMessage({ id: "ticketURL" })}
          name="ticketURL"
          showSkeleton={isLoading}
          disabled={disabled}
        />
        <FormInput<EditFixtureForm>
          layout="vertical"
          name="updated"
          label={formatMessage({ id: "updated" })}
          showSkeleton={isLoading}
          disabled
        />
      </StyledFixtureAdvancedSection>
    </div>
  );
};
